import {
  Box,
  Grid,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import NotificationButton from "../notification-button/notification-button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { logoutAction } from "../../../redux/actions/authAction";
import ConfirmDialog from "../../atoms/confirm-box/confirm-box";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { NotificationDrawerContent } from "./notification-drawe-content";
import { StoreSelectorDrawerContent } from "./store-selector-drawer-content";
import { MasterSliceActions } from "../../../redux/slice/masterSlice";

const StoreSelector: FC<{ onClick(): void }> = ({ onClick }) => {
  const { adminDashboardSelectedStore } = useAppSelector(
    (state) => state.master
  );
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        backgroundColor: "#ffffff",
        borderRadius: "0.57rem",
        px: "0.7rem",
        py: "0.3rem",
        gap: "0.5rem",
        "&:hover": { opacity: 0.8 },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          width: "2.28rem",
          height: "2.28rem",
          borderRadius: "0.36rem",
          backgroundColor: "#018752",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: "#ffffff", fontSize: "1rem", fontWeight: 700 }}>
          P
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "1.14rem", fontWeight: 500, color: "#515151" }}>
        {adminDashboardSelectedStore?.storeName ?? "Select Store"}
      </Typography>
      <KeyboardArrowDownIcon sx={{ fontSize: "1.8rem", color: "#515151" }} />
    </Box>
  );
};

export const AdminTopNav: FC = () => {
  const dispatch = useAppDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] =
    useState(false);
  const { storeSelectorDrawerOpen } = useAppSelector((state) => state.master);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logoutAction());
    handleClose();
  };

  const handleOpenConfirm = (openState: boolean) => {
    setConfirmOpen(openState);
  };

  const handleConfirm = () => {
    handleLogout();
    handleOpenConfirm(false);
  };

  const toggleNotificationDrawer = () => {
    setIsNotificationDrawerOpen((prevOpen) => !prevOpen);
  };

  const toggleStoreSelectorDrawer = () => {
    dispatch(
      MasterSliceActions.setStoreSelectorDrawerOpen(!storeSelectorDrawerOpen)
    );
  };

  return (
    <Grid
      sx={{
        display: "flex",
        py: "0.85rem",
        px: "1.43rem",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1rem",
      }}
    >
      {/* Left: Welcome text */}
      <Typography sx={{ fontSize: "1.43rem", fontWeight: 500, color: "#515151", whiteSpace: "nowrap" }}>
        Welcome Back!
      </Typography>

      {/* Center: Search bar */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          maxWidth: "46rem",
          borderRadius: "0.36rem",
          px: "0.86rem",
          py: "0.4rem",
          backgroundColor: "#ffffff",
        }}
      >
        <SearchIcon sx={{ color: "#b3b3b3", mr: "0.5rem", fontSize: "1.71rem" }} />
        <InputBase
          placeholder="Search"
          fullWidth
          sx={{ fontSize: "1.14rem", color: "#b3b3b3" }}
        />
      </Paper>

      {/* Right: Actions */}
      <Grid sx={{ display: "flex", alignItems: "center", gap: "0.71rem" }}>
        {/* Notification */}
        <NotificationButton onClick={toggleNotificationDrawer} />

        {/* Help */}
        <IconButton size="small">
          <HelpOutlineIcon sx={{ fontSize: "1.71rem", color: "#515151" }} />
        </IconButton>

        {/* Store selector */}
        <StoreSelector onClick={toggleStoreSelectorDrawer} />
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0rem 0rem 0.142857143rem rgba(0,0,0,0.1))",
              mt: 1.5,
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => handleOpenConfirm(true)}>Logout</MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmation"
        message="Are you sure you want to proceed?"
        onCancel={() => handleOpenConfirm(false)}
        onConfirm={handleConfirm}
      />

      {/* Notification drawer */}
      <CustomDrawer
        isOpen={isNotificationDrawerOpen}
        toggleDrawer={toggleNotificationDrawer}
      >
        <NotificationDrawerContent />
      </CustomDrawer>

      {/* Store selector drawer */}
      <CustomDrawer
        isOpen={storeSelectorDrawerOpen}
        toggleDrawer={toggleStoreSelectorDrawer}
        sx={{ width: 360, p: 0 }}
      >
        <StoreSelectorDrawerContent />
      </CustomDrawer>
    </Grid>
  );
};
